import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Badge, type Tone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Field, Input, Select } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { formatDateShort } from '@/lib/dates'
import { describeError, supabase, type Row } from '@/lib/supabase'

/**
 * The galleries, from the Studio side: status, the couple's link, password
 * and download PIN, the films (YouTube + Google Drive, decision 12), who
 * favourited what, and Deliver.
 *
 * Photos are uploaded by Claude from the Lightroom export with
 * `npm run gallery:upload` (studio/scripts/galleries/upload.mjs); this page
 * is for everything after.
 */

type Gallery = Row<'galleries'> & {
  photos: Array<{ count: number }>
  favourites: Array<{ count: number }>
  films: Array<Row<'gallery_films'>>
  job: { id: string; event_date: string } | null
}

const STATUS: Record<Row<'galleries'>['status'], { label: string; tone: Tone }> = {
  draft: { label: 'Uploading', tone: 'neutral' },
  ready: { label: 'Ready to check', tone: 'amber' },
  delivered: { label: 'Delivered', tone: 'sage' },
  archived: { label: 'Archived', tone: 'neutral' },
}

const KEY = ['galleries-admin'] as const

function useGalleries() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<Gallery[]> => {
      const { data, error } = await supabase
        .from('galleries')
        .select('*, photos:gallery_photos(count), favourites:gallery_favourites(count), films:gallery_films(*), job:jobs(id, event_date)')
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as unknown as Gallery[]
    },
  })
}

export function galleryUrl(slug: string): string {
  return `${window.location.origin}/g/${slug}`
}

export function GalleriesPage() {
  const query = useGalleries()

  if (query.isPending) return <Loading label="Loading galleries" />
  if (query.isError) {
    return (
      <Page>
        <ErrorState title="Galleries could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      </Page>
    )
  }

  const galleries = query.data ?? []

  return (
    <Page className="max-w-[64rem]">
      <PageHeader eyebrow="Delivery" title="Galleries" meta={<span>{galleries.length} galleries</span>} />
      {galleries.length === 0 ? (
        <EmptyState
          title="No galleries yet"
          body='Export the gallery from Lightroom into one folder per set, then ask Claude: "Upload the Anna & Ben gallery from <folder>".'
        />
      ) : (
        galleries.map((gallery) => <GalleryAdmin key={gallery.id} gallery={gallery} />)
      )}
    </Page>
  )
}

function GalleryAdmin({ gallery }: { gallery: Gallery }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(gallery.status !== 'delivered')
  const [password, setPassword] = useState('')
  const [pin, setPin] = useState('')
  const status = STATUS[gallery.status]
  const url = galleryUrl(gallery.slug)
  const photoCount = gallery.photos[0]?.count ?? 0
  const favouriteCount = gallery.favourites[0]?.count ?? 0

  const refresh = () => void queryClient.invalidateQueries({ queryKey: KEY })

  const update = useMutation({
    mutationFn: async (patch: Partial<Row<'galleries'>>) => {
      const { error } = await supabase.from('galleries').update(patch).eq('id', gallery.id)
      if (error) throw error
    },
    onSuccess: refresh,
    onError: (error) => toast.error('Could not save', { description: describeError(error) }),
  })

  const secret = useMutation({
    mutationFn: async ({ which, value }: { which: 'password' | 'pin'; value: string }) => {
      const { error } = await supabase.rpc('set_gallery_secret', { gallery: gallery.id, which, secret: value })
      if (error) throw error
    },
    onSuccess: (_d, v) => {
      refresh()
      toast.success(v.which === 'password' ? 'Gallery password saved' : 'Download PIN saved')
      if (v.which === 'password') setPassword('')
      else setPin('')
    },
    onError: (error) => toast.error('Could not save', { description: describeError(error) }),
  })

  function deliver() {
    const now = new Date()
    const expires = new Date(now.getTime() + 90 * 86400_000)
    update.mutate(
      { status: 'delivered', delivered_at: now.toISOString(), originals_expire_at: expires.toISOString() },
      { onSuccess: () => toast.success('Marked as delivered', { description: 'Send the couple the link, password and PIN. The gallery email arrives with the automated emails.' }) },
    )
  }

  return (
    <Section
      title={gallery.title}
      actions={
        <>
          <Badge tone={status.tone}>{status.label}</Badge>
          <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : 'Open'}
          </Button>
        </>
      }
    >
      <p className="text-[length:var(--text-sm)] text-taupe">
        {photoCount} photos
        {favouriteCount ? ` · ${favouriteCount} favourited` : ''}
        {gallery.delivered_at ? ` · delivered ${formatDateShort(gallery.delivered_at.slice(0, 10))}` : ''}
        {gallery.originals_expire_at ? ` · full resolution until ${formatDateShort(gallery.originals_expire_at.slice(0, 10))}` : ''}
        {gallery.job ? (
          <>
            {' · '}
            <Link to={`/weddings/${gallery.job.id}`} className="underline underline-offset-4">
              the job
            </Link>
          </>
        ) : null}
      </p>

      {open ? (
        <div className="mt-4 flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <a href={url} target="_blank" rel="noreferrer noopener" className="text-[length:var(--text-sm)] underline underline-offset-4">
              {url.replace(/^https?:\/\//, '')}
            </a>
            <Button size="sm" variant="ghost" onClick={() => void navigator.clipboard.writeText(url).then(() => toast.success('Link copied'))}>
              Copy link
            </Button>
            {gallery.status === 'ready' ? (
              <Button size="sm" variant="primary" onClick={deliver} loading={update.isPending}>
                Deliver
              </Button>
            ) : null}
            {gallery.status === 'delivered' && gallery.originals_expire_at ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  update.mutate(
                    { originals_expire_at: new Date(new Date(gallery.originals_expire_at ?? Date.now()).getTime() + 30 * 86400_000).toISOString() },
                    { onSuccess: () => toast.success('Full resolution extended by 30 days') },
                  )
                }
              >
                Extend full resolution 30 days
              </Button>
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Gallery password" hint={gallery.password_hash ? 'Set. Type a new one to change it.' : 'None: anyone with the link can view.'}>
              <div className="flex gap-2">
                <Input value={password} placeholder="e.g. anna2027" onChange={(e) => setPassword(e.target.value)} />
                <Button size="sm" disabled={!password.trim()} onClick={() => secret.mutate({ which: 'password', value: password })}>
                  Save
                </Button>
              </div>
            </Field>
            <Field label="Download PIN" hint={gallery.download_pin_hash ? 'Set. Type a new one to change it.' : 'None: downloads need no PIN.'}>
              <div className="flex gap-2">
                <Input value={pin} inputMode="numeric" maxLength={8} placeholder="4 digits" onChange={(e) => setPin(e.target.value)} />
                <Button size="sm" disabled={!pin.trim()} onClick={() => secret.mutate({ which: 'pin', value: pin })}>
                  Save
                </Button>
              </div>
            </Field>
          </div>

          <Films gallery={gallery} onChange={refresh} />

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="ghost" onClick={() => update.mutate({ downloads_enabled: !gallery.downloads_enabled })}>
              {gallery.downloads_enabled ? 'Switch downloads off' : 'Switch downloads on'}
            </Button>
            {gallery.status !== 'archived' ? (
              <Button size="sm" variant="ghost" onClick={() => update.mutate({ status: 'archived' })}>
                Take the gallery offline
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => update.mutate({ status: 'delivered' })}>
                Put it back online
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </Section>
  )
}

/** YouTube id from a pasted link (watch, youtu.be, shorts, embed) or the id itself. */
export function youtubeId(input: string): string | null {
  const text = input.trim()
  if (/^[A-Za-z0-9_-]{11}$/.test(text)) return text
  const match = text.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([A-Za-z0-9_-]{11})/)
  return match?.[1] ?? null
}

function Films({ gallery, onChange }: { gallery: Gallery; onChange: () => void }) {
  const [title, setTitle] = useState('Highlight film')
  const [kind, setKind] = useState('highlight')
  const [youtube, setYoutube] = useState('')
  const [drive, setDrive] = useState('')

  const add = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('gallery_films').insert({
        gallery_id: gallery.id,
        title: title.trim(),
        kind,
        youtube_id: youtubeId(youtube),
        drive_url: drive.trim() || null,
        position: gallery.films.length,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setYoutube('')
      setDrive('')
      onChange()
    },
    onError: (error) => toast.error('Could not add the film', { description: describeError(error) }),
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('gallery_films').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: onChange,
  })

  const valid = title.trim() !== '' && (youtubeId(youtube) !== null || drive.trim() !== '') && (youtube.trim() === '' || youtubeId(youtube) !== null)

  return (
    <div>
      <p className="label mb-2">Films (the Video tab)</p>
      {gallery.films.length === 0 ? (
        <p className="mb-3 text-[length:var(--text-sm)] text-taupe">No films: the Video tab is hidden.</p>
      ) : (
        <ul className="mb-3">
          {gallery.films.map((film) => (
            <li key={film.id} className="flex items-baseline justify-between gap-3 border-b py-1.5 text-[length:var(--text-sm)]">
              <span>
                {film.title}
                <span className="ml-2 text-[length:var(--text-2xs)] text-taupe">
                  {film.youtube_id ? 'YouTube' : ''}
                  {film.youtube_id && film.drive_url ? ' + ' : ''}
                  {film.drive_url ? 'Drive download' : ''}
                </span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => remove.mutate(film.id)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid items-end gap-2 sm:grid-cols-[minmax(0,1fr)_8rem_minmax(0,1fr)_minmax(0,1fr)_auto]">
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Kind">
          <Select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="highlight">Wedding highlight film</option>
            <option value="teaser">Trailer</option>
            <option value="other">Other (e.g. ceremony and speeches)</option>
          </Select>
        </Field>
        <Field label="YouTube link (unlisted)">
          <Input value={youtube} placeholder="https://youtu.be/…" onChange={(e) => setYoutube(e.target.value)} />
        </Field>
        <Field label="Google Drive link">
          <Input value={drive} placeholder="https://drive.google.com/…" onChange={(e) => setDrive(e.target.value)} />
        </Field>
        <Button size="sm" disabled={!valid} loading={add.isPending} onClick={() => add.mutate()}>
          Add film
        </Button>
      </div>
    </div>
  )
}
